import {
  Check,
  ContentCopyOutlined,
  ThumbDownAlt,
  ThumbDownAltOutlined,
  ThumbUpAlt,
  ThumbUpAltOutlined,
} from '@mui/icons-material';
import { Box, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { toast } from '@side/pantry';
import { useCallback, useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import {
  TrackReportsEvent,
  useTrackReportsAnalytics,
} from '$analytics/useTrackAnalytics/domains/reports';
import { type ChatMessage, INTRO_MESSAGE_ID } from './AIInsightsButton.helpers';

type FeedbackVote = 'positive' | 'negative' | null;

export const AIInsightsMessage = ({
  message,
  geminiInteractionId,
}: {
  message: ChatMessage;
  geminiInteractionId: string | null;
}) => {
  const isUser = message.role === 'user';
  const trackEvent = useTrackReportsAnalytics();
  const [vote, setVote] = useState<FeedbackVote>(null);
  const [isCopied, setIsCopied] = useState(false);
  const copyResetTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const showActions = !isUser && message.id !== INTRO_MESSAGE_ID;

  useEffect(
    () => () => {
      if (copyResetTimeoutRef.current) {
        clearTimeout(copyResetTimeoutRef.current);
      }
    },
    [],
  );

  const handleVote = useCallback(
    (nextVote: Exclude<FeedbackVote, null>) => {
      const selected = vote !== nextVote;
      const resolvedVote = selected ? nextVote : null;

      setVote(resolvedVote);

      trackEvent(
        nextVote === 'positive'
          ? TrackReportsEvent.AIInsightsResponsePositive
          : TrackReportsEvent.AIInsightsResponseNegative,
        {
          geminiInteractionId,
          messageId: message.id,
          selected,
        },
      );
    },
    [geminiInteractionId, message.id, trackEvent, vote],
  );

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(message.content);
      setIsCopied(true);
      if (copyResetTimeoutRef.current) {
        clearTimeout(copyResetTimeoutRef.current);
      }
      copyResetTimeoutRef.current = setTimeout(() => {
        setIsCopied(false);
      }, 2000);
      trackEvent(TrackReportsEvent.AIInsightsResponseCopy, {
        geminiInteractionId,
        messageId: message.id,
        copyStatus: 'success',
      });
    } catch {
      setIsCopied(false);
      toast('error', 'Failed to copy');
      trackEvent(TrackReportsEvent.AIInsightsResponseCopy, {
        geminiInteractionId,
        messageId: message.id,
        copyStatus: 'failed',
      });
    }
  }, [geminiInteractionId, message.content, message.id, trackEvent]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-end',
        alignItems: isUser ? 'flex-end' : 'flex-start',
        pr: isUser ? 0 : 4,
        pl: isUser ? 4 : 0,
      }}
    >
      <Box
        sx={(theme) => ({
          backgroundColor: isUser
            ? theme.lighten(theme.palette.other.spaceCadet, 0.1)
            : undefined,
          ...(isUser && {
            borderWidth: 1,
            borderStyle: 'solid',
            borderColor: theme.alpha(theme.palette.common.white, 0.35),
          }),
          px: isUser ? 2 : 0,
          py: isUser ? 1.25 : 0,
          borderRadius: '8px',
          maxWidth: '100%',
          wordBreak: 'break-word',
          color: 'common.white',
          border: message.isError
            ? `1px solid ${theme.palette.error.main}`
            : undefined,
          '& p': { marginTop: 0, marginBottom: theme.spacing(1) },
          '& p:last-child': { marginBottom: 0 },
          '& ul, & ol': {
            marginTop: 0,
            marginBottom: theme.spacing(1),
            paddingLeft: theme.spacing(3),
          },
          '& a': {
            color: theme.palette.secondary.light,
          },
        })}
      >
        {isUser ? (
          <Typography variant="body2">{message.content}</Typography>
        ) : (
          <Typography component="div" variant="body2">
            <ReactMarkdown>{message.content}</ReactMarkdown>
          </Typography>
        )}
      </Box>
      {showActions ? (
        <Stack direction="row" sx={{ mt: 0.5, color: 'common.white' }}>
          <Tooltip title={isCopied ? 'Copied' : 'Copy response'}>
            <IconButton
              size="small"
              aria-label={isCopied ? 'Copied response' : 'Copy response'}
              onClick={() => {
                void handleCopy();
              }}
              sx={{ color: 'inherit' }}
            >
              {isCopied ? (
                <Check sx={{ fontSize: 18 }} />
              ) : (
                <ContentCopyOutlined sx={{ fontSize: 18 }} />
              )}
            </IconButton>
          </Tooltip>
          <Tooltip title={vote === 'positive' ? 'Remove positive' : 'Positive'}>
            <IconButton
              size="small"
              aria-label="Positive feedback"
              onClick={() => handleVote('positive')}
              sx={{ color: 'inherit' }}
            >
              {vote === 'positive' ? (
                <ThumbUpAlt sx={{ fontSize: 18 }} />
              ) : (
                <ThumbUpAltOutlined sx={{ fontSize: 18 }} />
              )}
            </IconButton>
          </Tooltip>
          <Tooltip title={vote === 'negative' ? 'Remove negative' : 'Negative'}>
            <IconButton
              size="small"
              aria-label="Negative feedback"
              onClick={() => handleVote('negative')}
              sx={{ color: 'inherit' }}
            >
              {vote === 'negative' ? (
                <ThumbDownAlt sx={{ fontSize: 18 }} />
              ) : (
                <ThumbDownAltOutlined sx={{ fontSize: 18 }} />
              )}
            </IconButton>
          </Tooltip>
        </Stack>
      ) : null}
    </Box>
  );
};
